/**
 * Service GPS abstrait — même interface sur web et natif Capacitor.
 *
 * Web  → navigator.geolocation.watchPosition
 * Natif → @capacitor-community/background-geolocation (GPS écran éteint)
 *
 * Les imports Capacitor sont dynamiques avec @vite-ignore pour éviter
 * que Vite tente de les bundler (ils sont fournis par le runtime natif).
 */

import { isNative } from '@/utils/platform';

export interface GeoPosition {
  lat: number;
  lng: number;
  accuracy: number;
  speed: number | null; // m/s
  timestamp: number;
}

export type GeoCallback = (pos: GeoPosition) => void;
export type GeoErrorCallback = (err: string) => void;

export interface WatchOptions {
  desiredAccuracy?: number;
  distanceFilter?: number;
  stationaryRadius?: number;
  notificationTitle?: string;
  notificationText?: string;
  enableHighAccuracy?: boolean;
}

export interface WatchHandle {
  id: string | number;
  stop: () => Promise<void>;
}

export type PermissionStatus = 'granted' | 'denied' | 'prompt' | 'background_denied';

// ── Helpers pour charger les plugins Capacitor sans casser le build web ───────

async function loadCapacitorGeolocation() {
  try {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const mod = await import(/* @vite-ignore */ '@capacitor/geolocation');
    return mod.Geolocation ?? null;
  } catch {
    return null;
  }
}

async function loadBackgroundGeolocation() {
  try {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const mod = await import(/* @vite-ignore */ '@capacitor-community/background-geolocation');
    return mod.BackgroundGeolocation ?? null;
  } catch {
    return null;
  }
}

// ── Permission ────────────────────────────────────────────────────────────────

export async function requestGeoPermission(): Promise<PermissionStatus> {
  if (isNative()) {
    const Geolocation = await loadCapacitorGeolocation();
    if (!Geolocation) return 'denied';
    try {
      const result = await Geolocation.requestPermissions();
      if (result.location === 'granted') return 'granted';
      return result.location === 'denied' ? 'denied' : 'prompt';
    } catch {
      return 'denied';
    }
  }

  // Web
  if (!navigator.geolocation) return 'denied';
  return new Promise(resolve => {
    navigator.geolocation.getCurrentPosition(
      () => resolve('granted'),
      err => resolve(err.code === 1 ? 'denied' : 'prompt'),
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 30000 }
    );
  });
}

export async function checkGeoPermission(): Promise<PermissionStatus> {
  if (isNative()) {
    const Geolocation = await loadCapacitorGeolocation();
    if (!Geolocation) return 'denied';
    try {
      const result = await Geolocation.checkPermissions();
      if (result.location === 'granted') return 'granted';
      if (result.location === 'denied') return 'denied';
      return 'prompt';
    } catch {
      return 'denied';
    }
  }

  // Web
  if (navigator.permissions?.query) {
    try {
      const perm = await navigator.permissions.query({ name: 'geolocation' });
      if (perm.state === 'granted') return 'granted';
      if (perm.state === 'denied') return 'denied';
      return 'prompt';
    } catch {
      return 'prompt';
    }
  }
  return 'prompt';
}

// ── getCurrentPosition ────────────────────────────────────────────────────────

export async function getCurrentPosition(options?: WatchOptions): Promise<GeoPosition | null> {
  if (isNative()) {
    const Geolocation = await loadCapacitorGeolocation();
    if (!Geolocation) return null;
    try {
      const pos = await Geolocation.getCurrentPosition({
        enableHighAccuracy: options?.enableHighAccuracy ?? true,
        timeout: 10000,
      });
      return {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy ?? 999,
        speed: pos.coords.speed ?? null,
        timestamp: pos.timestamp,
      };
    } catch {
      return null;
    }
  }

  // Web
  return new Promise(resolve => {
    navigator.geolocation.getCurrentPosition(
      pos => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        speed: pos.coords.speed,
        timestamp: pos.timestamp,
      }),
      () => resolve(null),
      { enableHighAccuracy: options?.enableHighAccuracy ?? true, timeout: 10000, maximumAge: 0 }
    );
  });
}

// ── watchPosition ─────────────────────────────────────────────────────────────

export async function watchPosition(
  onPosition: GeoCallback,
  onError: GeoErrorCallback,
  options: WatchOptions = {}
): Promise<WatchHandle> {
  if (isNative()) {
    return watchNative(onPosition, onError, options);
  }
  return watchWeb(onPosition, onError, options);
}

// ── Natif : @capacitor-community/background-geolocation ──────────────────────

async function watchNative(
  onPosition: GeoCallback,
  onError: GeoErrorCallback,
  options: WatchOptions
): Promise<WatchHandle> {
  const BackgroundGeolocation = await loadBackgroundGeolocation();

  if (!BackgroundGeolocation) {
    // Plugin non disponible → fallback web
    return watchWeb(onPosition, onError, options);
  }

  try {
    const watchId = await BackgroundGeolocation.addWatcher(
      {
        desiredAccuracy: options.desiredAccuracy ?? 10,
        distanceFilter: options.distanceFilter ?? 10,
        stationaryRadius: options.stationaryRadius ?? 25,
        notificationTitle: options.notificationTitle ?? 'MyAutoLink',
        notificationText: options.notificationText ?? 'Trajet en cours — GPS actif',
        backgroundMessage: options.notificationText ?? 'Trajet en cours — GPS actif',
        backgroundTitle: options.notificationTitle ?? 'MyAutoLink',
        requestPermissions: true,
        highAccuracy: options.enableHighAccuracy ?? true,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (location: any, error: any) => {
        if (error) {
          onError(error.code === 'NOT_AUTHORIZED' ? 'background_denied' : (error.message ?? 'GPS error'));
          return;
        }
        if (location) {
          onPosition({
            lat: location.latitude,
            lng: location.longitude,
            accuracy: location.accuracy ?? 999,
            speed: location.speed ?? null,
            timestamp: location.time ?? Date.now(),
          });
        }
      }
    );

    return {
      id: watchId,
      stop: async () => {
        await BackgroundGeolocation.removeWatcher({ id: watchId });
      },
    };
  } catch {
    return watchWeb(onPosition, onError, options);
  }
}

// ── Web : navigator.geolocation.watchPosition ─────────────────────────────────

function watchWeb(
  onPosition: GeoCallback,
  onError: GeoErrorCallback,
  options: WatchOptions
): WatchHandle {
  const id = navigator.geolocation.watchPosition(
    pos => onPosition({
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      accuracy: pos.coords.accuracy,
      speed: pos.coords.speed,
      timestamp: pos.timestamp,
    }),
    err => onError(err.message),
    {
      enableHighAccuracy: options.enableHighAccuracy ?? true,
      maximumAge: 2000,
      timeout: 30000,
    }
  );

  return {
    id,
    stop: async () => {
      navigator.geolocation.clearWatch(id as number);
    },
  };
}
